import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from 'react-toastify';
import {
  buildIntegrationSettingsJson,
  EmbedCredentials,
  EmbedGuideStep,
  fillEmbedGuideSteps,
  fillEmbedScript,
  getEmbedPlatformGuide,
} from './embedCodeGuides';

type Props = {
  open: boolean;
  platformId: string;
  credentials: EmbedCredentials;
  onClose: () => void;
};

const copyText = async (value: string, label: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied.`, { position: 'top-right', autoClose: 2000 });
  } catch {
    toast.error(`Failed to copy ${label}.`, { position: 'top-right', autoClose: 2500 });
  }
};

const StepLink: React.FC<{ step: EmbedGuideStep; onNavigate?: () => void }> = ({
  step,
  onNavigate,
}) => {
  if (!step.link) return null;

  const label = step.linkText || 'Open link';
  const linkSx = {
    mt: 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.6,
    fontSize: 13,
    fontWeight: 700,
    color: '#2F7BC9',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  } as const;

  if (step.linkInternal) {
    return (
      <MuiLink
        component={RouterLink}
        to={step.link}
        onClick={onNavigate}
        sx={linkSx}
      >
        <OpenInNewIcon sx={{ fontSize: 15 }} />
        {label}
      </MuiLink>
    );
  }

  return (
    <MuiLink href={step.link} target="_blank" rel="noopener noreferrer" sx={linkSx}>
      <OpenInNewIcon sx={{ fontSize: 15 }} />
      {label}
    </MuiLink>
  );
};

const CredentialField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography
      sx={{
        mb: 0.75,
        fontSize: 12.5,
        fontWeight: 600,
        color: '#334155',
        textAlign: 'left',
      }}
    >
      {label}
    </Typography>
    <TextField
      fullWidth
      size="small"
      value={value}
      placeholder={`Enter ${label}`}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => copyText(value, label)}
              disabled={!value}
              sx={{ color: '#64748B' }}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          backgroundColor: '#F8FAFC',
          fontSize: 13.5,
          '& fieldset': { borderColor: '#E2E8F0' },
          '&:hover fieldset': { borderColor: '#CBD5E1' },
          '&.Mui-focused fieldset': { borderColor: '#007AFF' },
        },
      }}
    />
  </Box>
);

export default function EmbedCodeIntegrateDialog({
  open,
  platformId,
  credentials,
  onClose,
}: Props): React.JSX.Element {
  const [scriptOpen, setScriptOpen] = useState(false);

  const guide = useMemo(() => getEmbedPlatformGuide(platformId), [platformId]);
  const steps = useMemo(
    () => fillEmbedGuideSteps(guide, credentials),
    [guide, credentials]
  );
  const settingsJson = useMemo(
    () => buildIntegrationSettingsJson(guide.name, credentials),
    [guide.name, credentials]
  );
  const embedScript = useMemo(
    () => fillEmbedScript(guide, credentials),
    [guide, credentials]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: '92vh',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        },
      }}
    >
      <DialogContent
        sx={{
          p: { xs: 2.25, md: 3 },
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0, textAlign: 'left' }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#2F7BC9',
                mb: 0.75,
              }}
            >
              Get embed code · {guide.name}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 22, md: 26 },
                fontWeight: 800,
                color: '#0F2A4A',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              Integrate your widget
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#64748B', mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            mt: 2.5,
            mb: 3,
            px: 2,
            py: 1.5,
            borderRadius: 2.5,
            backgroundColor: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.18)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              minWidth: 34,
              borderRadius: '50%',
              backgroundColor: 'rgba(34, 197, 94, 0.16)',
              color: '#15803D',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontWeight: 700, color: '#0F2A4A', fontSize: 15 }}>
              Ready to integrate
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 13, color: '#64748B', lineHeight: 1.45 }}>
              {guide.readySubtitle}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 3.25, textAlign: 'left' }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#2F7BC9',
              mb: 0.6,
            }}
          >
            01 — Credentials
          </Typography>
          <Typography sx={{ fontWeight: 800, color: '#0F2A4A', fontSize: 18, mb: 1.75 }}>
            App credentials
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.75,
            }}
          >
            <CredentialField label="Organization ID" value={credentials.organizationId} />
            <CredentialField label="App ID" value={credentials.appId} />
            <CredentialField label="Variant ID" value={credentials.variantId} />
          </Box>
        </Box>

        <Box sx={{ mb: 3.25, textAlign: 'left' }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#2F7BC9',
              mb: 0.6,
            }}
          >
            02 — Setup
          </Typography>
          <Typography sx={{ fontWeight: 800, color: '#0F2A4A', fontSize: 18, mb: 1.5 }}>
            Follow these steps
          </Typography>

          <Stack spacing={1.25}>
            {steps.map((step, index) => (
              <Box
                key={`${step.title}-${index}`}
                sx={{
                  borderRadius: 2.5,
                  px: 1.75,
                  py: 1.5,
                  backgroundColor: '#f6f9fd',
                  border: '1px solid #e2f0ff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.35 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      minWidth: 28,
                      borderRadius: 1.25,
                      backgroundColor: '#e2f0ff',
                      color: '#087cff',
                      fontWeight: 800,
                      fontSize: 13,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 0.15,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, color: '#0F2A4A', fontSize: 14.5 }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ mt: 0.45, fontSize: 13, color: '#64748B', lineHeight: 1.5 }}>
                      {step.description}
                    </Typography>
                    <StepLink step={step} onNavigate={onClose} />
                    {step.code && (
                      <Box
                        component="pre"
                        sx={{
                          mt: 1.25,
                          mb: 0,
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: '#0B1220',
                          color: '#E2E8F0',
                          fontSize: 12,
                          lineHeight: 1.55,
                          overflowX: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          scrollbarWidth: 'none',
                          '&::-webkit-scrollbar': { display: 'none' },
                        }}
                      >
                        {step.code}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box sx={{ mb: 2, textAlign: 'left' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1.25,
            }}
          >
            {/* <Typography sx={{ fontWeight: 800, color: '#0F2A4A', fontSize: 16 }}>
              Integration settings
            </Typography> */}
            {/* <Button
              size="small"
              startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() => copyText(settingsJson, 'Integration settings')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: '#2F7BC9',
                minWidth: 0,
                px: 1,
              }}
            >
              Copy
            </Button> */}
          </Box>
          {/* <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 2.5,
              backgroundColor: '#0B1220',
              color: '#E2E8F0',
              fontSize: 12.5,
              lineHeight: 1.6,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {settingsJson}
          </Box> */}

          <Button
            onClick={() => setScriptOpen((prev) => !prev)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  transform: scriptOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 160ms ease',
                }}
              />
            }
            sx={{
              mt: 1.25,
              px: 0.5,
              textTransform: 'none',
              fontWeight: 700,
              color: '#2F7BC9',
              justifyContent: 'flex-start',
            }}
          >
            Get embed script
          </Button>
          <Collapse in={scriptOpen}>
            <Box
              component="pre"
              sx={{
                m: 0,
                mt: 1,
                p: 2,
                borderRadius: 2.5,
                backgroundColor: '#0B1220',
                color: '#E2E8F0',
                fontSize: 12.5,
                lineHeight: 1.6,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {embedScript || 'Embed script not available for this platform.'}
            </Box>
            {embedScript && (
              <Button
                size="small"
                startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={() => copyText(embedScript, 'Embed script')}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#2F7BC9',
                }}
              >
                Copy script
              </Button>
            )}
          </Collapse>
        </Box>

        <Box
          sx={{
            pt: 2,
            mt: 1,
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              borderColor: '#E2E8F0',
              color: '#334155',
              px: 2.5,
              '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' },
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
